import {
  action,
  computed,
  makeObservable,
  observable,
  ObservableMap,
} from 'mobx'

import * as e from '~/client/src/shared/stores/EventStore/eventConstants'

import { buildGlobeViewItems } from '../components/SitemapHelpers/models/buildGlobeViewItems'
import MapViewItemBase from '../components/SitemapHelpers/models/MapViewItemBase'
import GlobeView from '../models/GlobeView'
import LocationBase from '../models/LocationObjects/LocationBase'
import GlobeViewsStore from './domain/GlobeViews.store'
import LocationAttributesStore from './domain/LocationAttributes.store'
import SitemapItemsStore from './domain/SitemapItems.store'
import EventsStore from './EventStore/Events.store'
import InitialState from './InitialState'

export const DEFAULT_NAME = 'New Globe'
const POSITION_POLL_INTERVAL_MS = 10_000

export default class GlobeViewControlStore {
  @observable public selectedGlobeViewId: string = null
  private positionPollTimer: ReturnType<typeof setInterval> | null = null
  private isDisposed = false

  public constructor(
    protected readonly eventsStore: EventsStore,
    protected readonly globeViewsStore: GlobeViewsStore,
    protected readonly sitemapItemsStore: SitemapItemsStore,
    protected readonly locationAttributesStore: LocationAttributesStore,
  ) {
    makeObservable(this)
  }

  public get appState(): InitialState {
    return this.eventsStore.appState
  }

  public get selectedGlobeViewAssignedLocations(): LocationBase[] {
    if (!this.selectedGlobeView) {
      return []
    }
    return this.globeViewsStore.globeAssignedLocations(this.selectedGlobeView)
  }

  @computed
  public get selectedGlobeViewItems(): MapViewItemBase[] {
    return buildGlobeViewItems({
      globe: this.selectedGlobeView,
      getSitemapItem: id => this.sitemapItemsStore.byId.get(id),
      getDataObject: assignedId =>
        this.locationAttributesStore.hierarchyAllAttributesMap[assignedId],
      projectCenter: this.appState.projectAddress?.center,
    })
  }

  public selectInitialGlobe = (globe?: GlobeView): void => {
    this.selectGlobe(globe)
  }

  public get isLoading(): boolean {
    return (
      this.appState.loading.get(e.SAVE_GLOBE_VIEW) ||
      this.appState.loading.get(e.DELETE_GLOBE_VIEW)
    )
  }

  @computed
  public get allGlobeViews(): GlobeView[] {
    return this.globeViewsStore.list.slice()
  }

  @computed
  public get globeViewsMap(): ObservableMap<string, GlobeView> {
    return this.globeViewsStore.byId
  }

  @computed
  public get selectedGlobeView(): GlobeView {
    return this.globeViewsStore.byId.get(this.selectedGlobeViewId)
  }

  @computed
  public get globeViewsList(): GlobeView[] {
    return this.globeViewsStore.list
  }

  @action.bound
  public selectGlobeById(globeId: string): void {
    const globe = this.globeViewsStore.byId.get(globeId)
    this.selectGlobe(globe)
  }

  @action.bound
  public selectGlobe(globe: GlobeView): void {
    // Not deselectGlobe(): it stops the poll, which is not globe-scoped. A switch is now one per
    // row click, so restarting the interval each time would starve refreshPositions.
    this.selectedGlobeViewId = globe?.id ?? null
    if (!globe) {
      this.stopPositionPolling()
      return
    }

    this.startPositionPolling()
  }

  @action.bound
  public deselectGlobe(): void {
    this.selectedGlobeViewId = null
    this.stopPositionPolling()
  }

  // For a store that outlives the map reading it: pausable, unlike dispose().
  public stopPolling(): void {
    this.stopPositionPolling()
  }

  // Teardown only: must not clear the selection (that would fire reactions into an
  // unmounting tree) and must not route through deselectGlobe, which GlobeViewSetupStore
  // overrides without super.
  public dispose(): void {
    this.isDisposed = true
    this.stopPositionPolling()
  }

  private startPositionPolling(): void {
    if (this.isDisposed) {
      return
    }
    if (this.positionPollTimer) {
      return
    }
    this.positionPollTimer = setInterval(() => {
      if (document.visibilityState === 'visible') {
        this.locationAttributesStore.offloadingEquipmentsStore.refreshPositions()
      }
    }, POSITION_POLL_INTERVAL_MS)
  }

  private stopPositionPolling(): void {
    if (this.positionPollTimer) {
      clearInterval(this.positionPollTimer)
      this.positionPollTimer = null
    }
  }
}
